// ─── src/components/dashboard/MeetupProposalsTable.tsx ───────────────────────

import { useState } from "react";
import { Eye, Trash2, X, CheckCircle, XCircle, ShieldCheck, UserPlus, Users, Pencil } from "lucide-react";
import type { MeetupProposal } from "../../types/meetupProposal";
import type { MeetupEvent } from "../../types/meetupEvent";

interface Props {
  rows: MeetupProposal[];
  eventsByProposalId: Map<string, MeetupEvent>;
  onDelete: (id: string) => void;
  onApprove: (id: string, imageUrl?: string) => void;
  onReject: (id: string) => void;
  /** Admin edit — only fired for pending proposals. */
  onEdit: (proposal: MeetupProposal) => void;
  /** Admin edit — fired for published meetups (status === 'accepted'). */
  onEditEvent: (event: MeetupEvent) => void;
  onViewAttendees: (event: MeetupEvent) => void;
  isBusy: boolean;
}

function formatDate(iso: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

const STATUS_LABEL: Record<string, string> = {
  pending: "Waiting for Approval",
  accepted: "Published",
  rejected: "Rejected",
};

function StatusBadge({ status }: { status: string }) {
  const s = status || "pending";
  const cls =
    s === "accepted" ? "bg-emerald-500/15 text-emerald-400"
    : s === "rejected" ? "bg-red-500/15 text-red-400"
    : "bg-amber-500/15 text-amber-400";
  return <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${cls}`}>{STATUS_LABEL[s] ?? s}</span>;
}

function AttendeeStatusChip({ status }: { status: MeetupEvent["attendeeStatus"] }) {
  const cls =
    status === "Open" ? "bg-emerald-500/10 text-emerald-400"
    : status === "Waitlist" ? "bg-amber-500/10 text-amber-400"
    : "bg-brand-border text-brand-muted";
  return <span className={`px-1.5 py-0.5 rounded text-[10px] ${cls}`}>{status}</span>;
}

export function MeetupProposalsTable({
  rows, eventsByProposalId, onDelete, onApprove, onReject, onEdit, onEditEvent, onViewAttendees, isBusy,
}: Props) {
  const [selected, setSelected] = useState<MeetupProposal | null>(null);

  if (rows.length === 0) {
    return <div className="card p-10 text-center text-brand-muted">No proposals yet.</div>;
  }

  return (
    <>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-border">
                <th className="px-4 py-3 text-left text-brand-muted font-medium">#</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Host</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Governorate</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Event / Format</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Date</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Attendees</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Status</th>
                <th className="px-4 py-3 text-left text-brand-muted font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const status = r.status || "pending";
                const event = eventsByProposalId.get(r.id);
                return (
                  <tr key={r.id} className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors">
                    <td className="px-4 py-3 text-brand-muted">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <p className="text-white font-medium whitespace-nowrap flex items-center gap-1.5" dir="rtl">
                        <ShieldCheck size={13} className="text-emerald-400 shrink-0" />
                        {r.hostFullNameAr}
                      </p>
                      <p className="text-brand-muted text-xs">{r.hostEmail}</p>
                      {(r.coHostName || r.coHostEmail) && (
                        <p className="text-brand-muted text-xs flex items-center gap-1 mt-0.5" dir="rtl">
                          <UserPlus size={11} /> {r.coHostName || r.coHostEmail}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap" dir="rtl">{r.hostGovernorate}</td>
                    <td className="px-4 py-3 max-w-[200px]" dir="rtl">
                      {r.eventTitle && <p className="text-white truncate">{r.eventTitle}</p>}
                      <p className="text-brand-muted text-xs truncate">
                        {r.eventFormat === "قالبك الخاص (Your Own Format)" && r.eventFormatOther
                          ? r.eventFormatOther
                          : r.eventFormat}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                      {formatDate(r.proposedDate)}
                      <span className="text-xs block">{r.startTime}</span>
                    </td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                      {event ? (
                        <div className="flex items-center gap-1.5">
                          <span>{event.confirmedCount}/{event.capacity}</span>
                          <AttendeeStatusChip status={event.attendeeStatus} />
                        </div>
                      ) : (
                        r.expectedAttendees
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelected(r)} title="View details"
                          className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors">
                          <Eye size={14} />
                        </button>
                        {event && (
                          <button onClick={() => onViewAttendees(event)} title="View attendees"
                            className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors">
                            <Users size={14} />
                          </button>
                        )}
                        {status === "pending" && (
                          <button onClick={() => onEdit(r)} disabled={isBusy} title="Edit proposal"
                            className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors disabled:opacity-40">
                            <Pencil size={14} />
                          </button>
                        )}
                        {status === "accepted" && event && (
                          <button
                            onClick={() => onEditEvent(event)}
                            disabled={isBusy}
                            title="Edit published meetup"
                            className="p-1.5 rounded-lg text-brand-muted hover:text-brand-green hover:bg-brand-green/10 transition-colors disabled:opacity-40"
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                        {status !== "accepted" && (
                          <button
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Approve and publish this meetup from ${r.hostFullNameAr}?`,
                                )
                              ) {
                                onApprove(r.id);
                              }
                            }}
                            disabled={isBusy}
                            title="Approve & publish"
                            className="p-1.5 rounded-lg text-brand-muted hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-40"
                          >
                            <CheckCircle size={14} />
                          </button>
                        )}
                        {status !== "rejected" && (
                          <button onClick={() => onReject(r.id)} disabled={isBusy} title={status === "accepted" ? "Unpublish" : "Reject"}
                            className="p-1.5 rounded-lg text-brand-muted hover:text-amber-400 hover:bg-amber-500/10 transition-colors disabled:opacity-40">
                            <XCircle size={14} />
                          </button>
                        )}
                        <button onClick={() => onDelete(r.id)} disabled={isBusy} title="Remove"
                          className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <DetailModal
          row={selected}
          event={eventsByProposalId.get(selected.id) ?? null}
          onViewAttendees={onViewAttendees}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

function Field({ label, value, rtl }: { label: string; value?: string | null; rtl?: boolean }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-brand-muted text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className="text-white text-sm whitespace-pre-wrap leading-relaxed" dir={rtl ? "rtl" : "ltr"}>
        {value}
      </p>
    </div>
  );
}

function DetailModal({
  row, event, onViewAttendees, onClose,
}: {
  row: MeetupProposal;
  event: MeetupEvent | null;
  onViewAttendees: (event: MeetupEvent) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="card w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-brand-border">
          <div dir="rtl">
            <p className="text-white font-bold text-lg flex items-center gap-2">
              <ShieldCheck size={16} className="text-emerald-400" />
              {row.hostFullNameAr}
            </p>
            <p className="text-brand-muted text-sm" dir="rtl">
              {row.hostGovernorate} · <span dir="ltr">{row.hostEmail}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={row.status || "pending"} />
            <button onClick={onClose} className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Event & RSVPs (only once published) */}
          {event && (
            <div className="rounded-lg border border-brand-border overflow-hidden flex flex-col sm:flex-row">
              <img src={event.imageUrl} alt="" className="w-full sm:w-40 h-28 sm:h-auto object-cover bg-brand-navy shrink-0" />
              <div className="p-4 flex-1 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <p className="text-brand-muted text-xs uppercase tracking-wider mb-1">Published event</p>
                  <p className="text-white font-medium">{event.title}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-brand-muted text-sm">{event.confirmedCount}/{event.capacity} confirmed</span>
                    <AttendeeStatusChip status={event.attendeeStatus} />
                    {event.waitlistCount > 0 && (
                      <span className="text-amber-400 text-xs">+{event.waitlistCount} waitlisted</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => onViewAttendees(event)}
                  className="btn-primary text-sm flex items-center gap-1.5 shrink-0"
                >
                  <Users size={14} /> View Attendees
                </button>
              </div>
            </div>
          )}

          {/* Host (verified catalyst) */}
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
            <p className="text-emerald-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <ShieldCheck size={13} /> Verified host (catalyst directory)
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Field label="Gender" value={row.hostGender} rtl />
              <Field label="Phone" value={row.hostPhone} />
              <Field label="City / area" value={row.hostCityArea} rtl />
              <Field label="Submitted" value={formatDate(row.createdAt)} />
            </div>
          </div>

          {/* Co-hosts */}
          {(row.coHostName || row.coHostEmail || row.additionalCoHosts) && (
            <div className="rounded-lg border border-brand-border bg-brand-navy p-4">
              <p className="text-brand-muted text-xs uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <UserPlus size={13} /> Co-organizers (unverified)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Name" value={row.coHostName} rtl />
                <Field label="Email" value={row.coHostEmail} />
              </div>
              {row.additionalCoHosts && (
                <div className="mt-4">
                  <Field label="Additional co-organizers" value={row.additionalCoHosts} rtl />
                </div>
              )}
            </div>
          )}

          {/* Event */}
          <div className="pt-4 border-t border-brand-border space-y-4">
            <Field label="Proposed title" value={row.eventTitle} rtl />
            {row.targetAudience?.trim() && (
              <Field label="Target audience" value={row.targetAudience} rtl />
            )}
          </div>

          <div className="pt-4 border-t border-brand-border grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Format" value={row.eventFormat} rtl />
            <Field label="Format (other)" value={row.eventFormatOther} rtl />
            <Field label="Structure" value={row.structure} rtl />
            <Field label="Mode" value={row.mode ? ({ in_person: "In person", online: "Online", hybrid: "Hybrid" })[row.mode] : null} />
            <Field label="Venue" value={row.venue} rtl />
            <Field label="Venue status" value={row.venueStatus} rtl />
            <Field label="Meeting link (private)" value={row.meetingUrl} />
            <Field label="Date" value={formatDate(row.proposedDate)} />
            <Field label="Start time" value={row.startTime} />
            <Field label="Duration (h)" value={row.durationHours} />
            <Field label="Expected attendees" value={row.expectedAttendees} />
          </div>

          <div className="pt-4 border-t border-brand-border space-y-4">
            <Field label="Motivation & impact" value={row.motivation} rtl />
            <Field label="Agenda" value={row.agenda} rtl />
            <Field label="Series details" value={row.seriesDetails} rtl />
            <Field label="Speakers" value={row.speakers} rtl />
            <Field label="Other details" value={row.otherDetails} rtl />
          </div>

          <div className="pt-4 border-t border-brand-border space-y-4">
            <Field label="Partner support" value={row.partnerSupport} rtl />
            <Field label="Partnership & cost coverage" value={row.partnershipDetails} rtl />
            <Field label="Logistics support needed" value={row.logisticsSupport} rtl />
            <div>
              <p className="text-brand-muted text-xs uppercase tracking-wider mb-1">Facebook event requested</p>
              <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium ${
                row.wantsFacebookEvent
                  ? "bg-emerald-500/15 text-emerald-400"
                  : "bg-brand-border text-brand-muted"
              }`}>
                {row.wantsFacebookEvent ? "Yes" : "No"}
              </span>
            </div>
          </div>

          {/* Commitments */}
          <div className="pt-4 border-t border-brand-border">
            <p className="text-brand-muted text-xs uppercase tracking-wider mb-2">
              Commitment charter ({row.commitments?.length ?? 0}/5)
            </p>
            <ul className="space-y-1.5" dir="rtl">
              {(row.commitments ?? []).map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-white">
                  <CheckCircle size={14} className="text-emerald-400 shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
